/**
 * lib/api.ts — thin wrapper around fetch() for the Cloudflare Router
 * worker. Pulls the Bearer token from the Zustand store automatically.
 */
import { useStore } from '@/store';
import { credentialHeadersFor, parseVaultSession, type UnlockedMap } from '@/lib/brokerCreds';
import { describeApiError, type ErrorKind, type RouterErrorBody } from '@/lib/apiErrors';

export const API_BASE = (import.meta.env.VITE_API_BASE ?? 'https://api.omnistrat.ai') as string;

class HttpError extends Error {
  status: number;
  /** body.code when present (e.g. OWN_ACCOUNT_REQUIRED), else the router's error code. */
  code?:  string;
  /** The router's error code (body.error), e.g. TRADING_MODE_MISMATCH. */
  error?: string | null;
  kind?:  ErrorKind;
  body?:  RouterErrorBody;
  constructor(status: number, message: string, code?: string,
              extra?: { error?: string | null; kind?: ErrorKind; body?: RouterErrorBody }) {
    super(message);
    this.status = status;
    this.code = code;
    this.error = extra?.error ?? null;
    this.kind = extra?.kind;
    this.body = extra?.body;
  }
}
export { HttpError };

/**
 * Paths whose most recent response was SYNTHETIC (server-side demo data).
 *
 * The server can fabricate options chains and news headlines when
 * TERMINAL_DEMO_DATA is on. Nothing in the payload distinguished those
 * from real market data, so the UI rendered invented Greeks, GEX,
 * max-pain and sentiment-scored headlines with total confidence. The
 * server now sets `x-omnistrat-synthetic: 1`; record it here so any
 * component can badge its own data without extra plumbing.
 */
const syntheticPaths = new Set<string>();

/** True when the last response for a path prefix was fabricated. */
export function isSynthetic(pathPrefix: string): boolean {
  for (const p of syntheticPaths) if (p.startsWith(pathPrefix)) return true;
  return false;
}

// WHICH CREDENTIAL GOES ON WHICH REQUEST is decided in lib/brokerCreds.ts,
// and only there (targetBrokersFor / credentialHeadersFor). The rules, in
// short: per-broker named headers (x-broker-<name>-key/-secret/-mode), only on
// the routes the router reads them on, only for the broker(s) that request
// targets, and NEVER the legacy unscoped x-broker-key -- the router refuses
// it (400 BROKER_HEADER_UNSCOPED) because it used to fan one Alpaca secret out
// to IBKR and Tradovate on every blotter refresh. Credentials never go on a
// query string: a live trading credential must not land in an access log,
// browser history or a Referer header.
const SESSION_VAULT_KEY = 'terminal-vault-unlocked';

function unlockedBrokers(): UnlockedMap {
  // Read the vault here rather than importing lib/vault -- vault.ts imports
  // API_BASE from this module, so that import would be circular. The parser
  // is shared (lib/brokerCreds.ts parseVaultSession) and accepts both the v2
  // map and the pre-2026-08-03 single-entry shape: failing to read the old
  // shape looks exactly like a locked vault.
  try { return parseVaultSession(sessionStorage.getItem(SESSION_VAULT_KEY)); }
  catch { return {}; }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = useStore.getState().token;
  const method = (init?.method ?? 'GET').toUpperCase();
  const brokerHeaders = credentialHeadersFor(method, path, unlockedBrokers());

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept:         'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...brokerHeaders,
    ...(init?.headers ?? {}),
  };
  const resp = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({})) as RouterErrorBody;
    // The router's CODE plus a sentence that says what happened and what was
    // NOT done (lib/apiErrors.ts). Several B6 codes mean "we could not tell"
    // (503 ENTITLEMENTS_UNAVAILABLE, DESIGNATIONS_UNAVAILABLE): a bare code,
    // or an empty table, would render that as a fact.
    const d = describeApiError(resp.status, body, resp.statusText);
    throw new HttpError(resp.status, d.message, d.subcode ?? d.code ?? undefined, {
      error: d.code, kind: d.kind, body,
    });
  }
  const key = path.split('?')[0];
  if (resp.headers.get('x-omnistrat-synthetic') === '1') syntheticPaths.add(key);
  else syntheticPaths.delete(key);
  return resp.json() as Promise<T>;
}
